import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import FontAwesomeIcon from "../../components/FontAwesomeIcon";
import BookingModal from '../../components/BookingModal';
import FAQSection from '../../components/FAQSection';

const ChimpHabituation = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const faqs = [
    {
      question: "How is this different from regular chimpanzee trekking?",
      answer: "Regular trekking gives you one hour with fully habituated chimps in groups of up to 8 people. The Habituation Experience offers 6-8 hours with semi-habituated chimps in groups of only 6 people. You observe the complete daily cycle from wake-up to nest-building, participate in research activities, and gain far deeper behavioral insights. Both cost the same permit fee, making habituation exceptional value for the extended time and intimate experience."
    },
    {
      question: "What fitness level do I need?",
      answer: "You need good to very good fitness. Expect to hike 5-10+ kilometers over 8+ hours through challenging terrain—steep slopes, muddy trails, dense vegetation, and stream crossings. The pace varies as you follow unpredictable chimp movements. If you regularly hike, do cardio exercise, and are comfortable spending a full day outdoors, you'll manage. However, this is significantly more demanding than standard trekking due to the extended duration. Sedentary individuals or those with mobility issues will find this extremely difficult."
    },
    {
      question: "Will I definitely see chimpanzees?",
      answer: "Yes, success rates exceed 95%. Researchers track chimp communities daily and know their ranging patterns, nesting sites, and preferred feeding areas. You'll start at their overnight nests, so finding them is almost guaranteed. The real question isn't whether you'll see chimps, but how much amazing behavior you'll witness during your extended time with them."
    },
    {
      question: "Can children participate?",
      answer: "The minimum age is 12 years (some sources say 15 years—we'll confirm current regulations when booking). Even for teenagers, this is a demanding experience requiring patience, silence, and physical endurance. The long duration, challenging terrain, and need for quiet observation make it unsuitable for most children. Regular chimpanzee trekking (1 hour) is a better option for families with younger children."
    },
    {
      question: "What's the best time of year for habituation?",
      answer: "The experience runs year-round, with each season offering advantages. Dry seasons (June-September, December-February) provide easier hiking conditions and more comfortable weather, though chimps may range farther searching for food. Wet seasons (March-May, October-November) mean muddier trails but chimps often stay in smaller areas due to abundant food, potentially making them easier to locate. Kibale's equatorial location means temperatures remain fairly constant year-round."
    },
    {
      question: "Should I hire a porter?",
      answer: "Highly recommended! A porter carries your daypack, freeing you to focus on observing chimps and taking photos without the burden of gear. They also provide physical assistance on steep or slippery sections and help you navigate difficult terrain. Porter hire directly supports local communities—many are former poachers who now earn sustainable income from conservation tourism. The service is affordable and makes a significant difference to your comfort during the long day."
    },
    {
      question: "Can I do both habituation and regular trekking?",
      answer: "Absolutely! Many enthusiasts do habituation one day for deep immersion, then regular trekking another day with a different habituated community for comparison. This multi-day approach provides a comprehensive Kibale primate experience. Consider spending 3-4 days in the area to allow time for both activities plus rest. You might also add Bigodi Wetland walks or nocturnal forest walks to round out your visit."
    },
    {
      question: "What if I get sick or need to leave early?",
      answer: "If you're unwell (cold, flu, stomach issues), you cannot participate—chimps are highly susceptible to human diseases and your illness could devastate the community. Permits are non-refundable, so ensure you're healthy before your scheduled date. If you need to leave early during the trek due to exhaustion or injury, rangers will escort you back to headquarters, but you won't receive a refund. This is why good fitness preparation is essential."
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1540573133985-87b6da6d54a9?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Chimpanzee Habituation Experience
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Spend a full day with researchers observing semi-habituated chimps in Kibale Forest
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                </div>
                <span className="text-lg">(Exclusive experience)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-microscope" className="w-4 h-4 mr-2" />
                Research Experience
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />
                Full Day (6-8 Hours)
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-users" className="w-4 h-4 mr-2" />
                Max 6 People
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Habituation Permit
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Description Section */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                The Ultimate Chimpanzee Encounter
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The Chimpanzee Habituation Experience (CHEX) offers participants a rare opportunity to spend an entire day alongside researchers and rangers as they work with semi-habituated chimpanzee communities in Kibale Forest. Starting before dawn, participants venture into the forest's misty depths not as tourists on a quick wildlife viewing, but as active participants in conservation research, gradually teaching these wild primates to tolerate human presence.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Unlike standard chimpanzee trekking where you spend just one hour with fully habituated chimps, the habituation experience allows you to observe a chimpanzee community from the moment they wake in their overnight nests around 6:00 AM until they construct new nests for the evening around 7:00 PM. This extended immersion—typically 6 to 8 hours of direct observation—reveals the complete daily rhythm of chimpanzee life: morning grooming rituals, foraging expeditions through the forest, social interactions ranging from playful to aggressive, tool use for extracting termites and cracking nuts, territorial patrols, and the fascinating evening nest-building behavior.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  What makes this experience profoundly special is the intimate group size—only 6 permits are issued per day compared to 72 for regular trekking. This means you're part of a tiny team moving flexibly through the forest, able to pause when chimps engage in interesting behaviors, adjust your position for better viewing, and ask researchers detailed questions about what you're witnessing. The semi-habituated status of these chimps means they're still learning to accept human observers, so their behavior feels more authentic and unpredictable than fully habituated groups. They might approach surprisingly close out of curiosity, or suddenly flee if uncomfortable—every moment offers genuine insight into wild chimpanzee psychology.
                </p>

                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Why Choose Habituation Over Standard Trekking?</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The habituation experience costs the same as standard chimpanzee trekking but delivers exponentially more value. Instead of rushing to find chimps for your allocated hour, you have the entire day to observe the full spectrum of behaviors. You'll witness feeding strategies as chimps select ripe fruits while rejecting unripe ones, watch mothers tenderly care for infants, see juveniles engage in rough-and-tumble play that teaches crucial social skills, and observe the complex dominance hierarchies that structure chimpanzee society.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The research component adds tremendous depth to your experience. Rangers and scientists explain individual chimps' personalities, family relationships, and ongoing behavioral studies. You might assist with data collection—recording feeding times, noting vocalizations, or helping identify individual chimps by their unique facial features and body markings. This participatory element transforms you from passive observer to active contributor in conservation science, making the experience both educational and meaningful.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  For photographers and wildlife enthusiasts, the extended time is invaluable. You'll have multiple opportunities to capture different behaviors, experiment with various lighting conditions as the day progresses, and wait patiently for that perfect moment when a chimp makes eye contact or performs a remarkable behavior. The relaxed pace means you're not constantly checking your watch, anxious about your hour expiring—instead, you can fully immerse yourself in the experience.
                </p>
              </div>
            </section>

            {/* Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                What Makes This Experience Extraordinary
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { icon: 'fa-sun', title: 'Dawn to Dusk Observation', description: 'Witness chimps waking in their nests, spending the day foraging and socializing, then building new nests at sunset—the complete daily cycle' },
                  { icon: 'fa-microscope', title: 'Active Research Participation', description: 'Join scientists in the field, learn habituation methodology, assist with data collection, and understand ongoing conservation studies' },
                  { icon: 'fa-users-between-lines', title: 'Intimate Group Size', description: 'Maximum 6 people per session versus 72 for standard trekking—ensuring personal attention and flexible movement' },
                  { icon: 'fa-brain', title: 'Behavioral Insights', description: 'Observe tool use, problem-solving, social learning, conflict resolution, and communication—revealing remarkable intelligence' },
                  { icon: 'fa-baby', title: 'Family Dynamics', description: 'Watch mothers nursing and protecting infants, juveniles learning from adults, and complex social bonds forming and strengthening' },
                  { icon: 'fa-tree', title: 'Nest-Building Ritual', description: 'Rare opportunity to observe evening nest construction—chimps carefully selecting branches and weaving sleeping platforms' },
                  { icon: 'fa-camera', title: 'Photography Paradise', description: 'Extended time allows multiple behaviors, varied lighting, and patience for perfect shots—photographers dream experience' },
                  { icon: 'fa-leaf', title: 'Conservation Impact', description: 'Your permit directly funds habituation research, anti-poaching patrols, and community programs protecting Kibale ecosystem' }
                ].map((highlight, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                      <p className="text-gray-700">{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Your Full-Day Habituation Journey
              </h2>
              <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 text-lg">
                  <strong>⚠️ Important Note:</strong> This itinerary is tentative and highly flexible. Chimpanzee movements are unpredictable, so timing may vary significantly. We customize the experience based on where the chimps are located and your preferences.
                </p>
              </div>
              <div className="space-y-6">
                {[
                  { time: '05:30', title: 'Pre-Dawn Departure', desc: 'Leave your lodge in darkness and drive to Kanyanchu Visitor Centre. The early start ensures you reach the chimps before they wake and begin their day.' },
                  { time: '06:00', title: 'Briefing & Team Assembly', desc: 'Meet your research team—scientists, rangers, and trackers. Learn about the specific chimp community you\'ll observe, safety protocols, and habituation methodology. Receive your walking stick and prepare for the trek.' },
                  { time: '06:30', title: 'Trek to Nest Sites', desc: 'Hike through pre-dawn forest to where the community nested the previous evening. Trackers use GPS data and knowledge of chimp ranging patterns to locate them. The forest atmosphere at this hour is magical—mist rising, birds beginning to call, and anticipation building.' },
                  { time: '07:00', title: 'Morning Wake-Up', desc: 'Arrive quietly at the nest trees and wait as chimps stir. You\'ll hear soft vocalizations, see stretching limbs, and watch as they carefully descend from their overnight sleeping platforms. This intimate moment is rarely witnessed by tourists.' },
                  { time: '07:30', title: 'Morning Grooming Session', desc: 'After descending, chimps typically engage in extensive grooming—a crucial social bonding activity. Watch mothers groom infants, friends reinforce alliances, and subordinates appease dominant individuals through this tactile communication.' },
                  { time: '08:30', title: 'Foraging Begins', desc: 'The community moves through the forest searching for food. Observe their remarkable knowledge of fruiting trees, watch them use tools to extract termites from mounds, and see them crack nuts with stone hammers—behaviors demonstrating sophisticated intelligence.' },
                  { time: '10:00', title: 'Social Interactions', desc: 'As the morning progresses, you\'ll witness the full range of chimp social behavior: juveniles playing, males engaging in dominance displays, possible mating attempts, and the constant vocal communication that maintains group cohesion.' },
                  { time: '12:00', title: 'Midday Rest & Lunch', desc: 'Chimps often rest during the hottest part of the day, grooming or napping in the shade. This is your opportunity to eat your packed lunch while continuing observations. Researchers explain what you\'ve seen and answer questions.' },
                  { time: '14:00', title: 'Afternoon Activity Resumes', desc: 'As temperatures cool, chimps become active again. You might witness territorial patrols along community boundaries, hunting attempts (chimps occasionally hunt smaller primates), or conflicts between individuals over food or social status.' },
                  { time: '16:00', title: 'Late Afternoon Foraging', desc: 'The community makes a final foraging push before evening, seeking high-energy foods to sustain them through the night. Their food selection becomes more strategic as they prepare for the overnight fast.' },
                  { time: '18:30', title: 'Nest-Building Ritual', desc: 'As dusk approaches, chimps begin selecting suitable trees for overnight nests. Watch as they carefully bend and weave branches into secure sleeping platforms—a fascinating behavior that demonstrates planning and construction skills. Each chimp builds their own nest, with mothers creating larger platforms to share with infants.' },
                  { time: '19:30', title: 'Return Trek', desc: 'Once chimps settle into their nests for the night, you begin the hike back to headquarters, often in darkness using headlamps. The forest sounds completely different at night—a fitting end to an extraordinary day.' }
                ].map((step, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-xl">
                          {step.time}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                        <p className="text-gray-700">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* What to Bring */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Essential Packing Guide
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-shirt" className="w-6 h-6" />
                    Clothing Essentials
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Long-sleeved shirt & pants:</strong> Protect against thorns, insects, and stinging nettles. Choose lightweight, quick-dry fabrics in neutral colors (green, brown, khaki)—avoid bright colors that might disturb chimps.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Waterproof rain jacket:</strong> Kibale receives rain year-round. A lightweight, breathable rain jacket is essential—you'll be hiking all day and need protection without overheating.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Sturdy hiking boots:</strong> Waterproof boots with good ankle support and aggressive tread. The forest floor is muddy, slippery, and uneven—proper footwear prevents injuries and keeps you comfortable during 8+ hours of hiking.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Thick socks & gaiters:</strong> Long socks to tuck pants into, preventing insects from crawling up your legs. Gaiters keep mud and debris out of your boots.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Garden gloves:</strong> Protect hands when grabbing vegetation for support on steep sections or pushing through dense undergrowth.</span>
                    </li>
                  </ul>
                </div>

                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-camera" className="w-6 h-6" />
                    Photography & Gear
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Camera with telephoto lens:</strong> A 70-200mm or 100-400mm lens is ideal. You'll maintain the required 8-meter distance, so zoom capability is essential for good shots. Bring extra batteries and memory cards—you'll take hundreds of photos.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Binoculars:</strong> Essential for observing chimps high in the canopy or at a distance. 8x42 or 10x42 magnification works well in forest conditions.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Waterproof camera bag:</strong> Protect expensive equipment from rain and humidity. A backpack-style bag distributes weight evenly during long hikes.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Headlamp with extra batteries:</strong> You'll likely return after dark. A hands-free light is essential for safe navigation on forest trails.</span>
                    </li>
                  </ul>
                </div>

                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-bottle-water" className="w-6 h-6" />
                    Food & Hydration
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>2-3 liters of water:</strong> Hydration is critical during 8+ hours of hiking in humid forest conditions. Use a hydration bladder or multiple bottles.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Packed lunch:</strong> Your lodge can prepare this. Choose non-perishable, energy-dense foods like sandwiches, nuts, dried fruit, and energy bars. You'll eat during the midday rest period.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Energy snacks:</strong> Trail mix, chocolate, glucose tablets for quick energy boosts throughout the day.</span>
                    </li>
                  </ul>
                </div>

                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-kit-medical" className="w-6 h-6" />
                    Health & Safety
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Insect repellent (DEET 30%+):</strong> Forest mosquitoes, tsetse flies, and ants are persistent. Reapply throughout the day.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Sunscreen & lip balm:</strong> Even in forest shade, UV exposure accumulates over a full day.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Personal first-aid kit:</strong> Bandages, blister treatment, pain relievers, any prescription medications, and anti-diarrheal medicine.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Face mask & hand sanitizer:</strong> Required for chimp protection. Chimps share 98.7% of our DNA and are highly susceptible to human diseases.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* FAQs */}
            <FAQSection faqs={faqs} title="Your Questions Answered" />

            {/* Related Activities */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                More Kibale Forest Adventures
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/chimpanzee-trekking" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1540573133985-87b6da6d54a9?w=800"
                      alt="Chimp Trekking"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Standard Chimpanzee Trekking</h3>
                    <p className="text-gray-700 mb-4">The classic 1-hour encounter with fully habituated chimps—perfect for families and those with less time.</p>
                    <p className="text-safari-gold font-bold">Permit fees apply</p>
                  </div>
                </Link>

                <Link to="/activities/chimp-kyambura-gorge" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1551033406-611cf9a28f67?w=800"
                      alt="Kyambura Gorge"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Kyambura Gorge Chimps</h3>
                    <p className="text-gray-700 mb-4">Trek the dramatic "Valley of Apes" in Queen Elizabeth National Park for a different chimp experience.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> Full Day (6-8 hours)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Year-round</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Permit Fee:</strong> Contact for current rates</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                    <span><strong>Difficulty:</strong> Strenuous</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                    <span><strong>Group:</strong> Max 6 people</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-user" className="w-6 h-6 text-safari-green" />
                    <span><strong>Min Age:</strong> 12-15 years (varies)</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Habituation
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Chimpanzee habituation permit (mandatory)",
                    "Park entrance fees",
                    "Full day with research team",
                    "Armed ranger escort",
                    "6-8 hours with chimps",
                    "Certificate of participation"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Transport to Kibale",
                    "Accommodation before/after",
                    "Meals (pack your own lunch)",
                    "Porter hire (highly recommended)",
                    "Tips for guides and porters"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Fitness
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Fitness:</strong> High. 6-8 hours hiking in difficult terrain.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Health:</strong> No infectious diseases. Chimps are susceptible to human illnesses.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Vaccination:</strong> Yellow fever certificate required for Uganda entry.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Chimpanzee Habituation Experience"
      />
    </div>
  );
};

export default ChimpHabituation;
